/**
 * PhaseMap — orienting 4-phase dot map for the RETURN hero per spec §3.4.
 *
 * Phase 4c: consumes the explicit phase → week → task tree. Each phase
 * shows one dot + cumulative completion ratio across its 3 weeks. Status
 * is derived from completion data: if all tasks done → done; if some done
 * → current; otherwise upcoming. Only one phase is "current" — the first
 * one with incomplete tasks; later phases stay "upcoming" even if they
 * have stray completions (rare but possible if a user jumped ahead).
 */

import React from "react";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { RoadmapPhase, RoadmapTaskCompletion } from "../../api/client";

interface PhaseMapProps {
  phases: RoadmapPhase[];
  completions: RoadmapTaskCompletion[];
}

type Status = "done" | "current" | "upcoming";

function flattenTasks(phase: RoadmapPhase): { id: string }[] {
  return (phase.weeks ?? []).flatMap((w) => w.tasks ?? []);
}

export default function PhaseMap({ phases, completions }: PhaseMapProps) {
  const theme = useTheme();
  const { t } = useTranslation("home");

  const doneIds = React.useMemo(
    () => new Set(completions.map((c) => c.task_id)),
    [completions],
  );

  // Per-phase totals + done count.
  const stats = React.useMemo(
    () =>
      phases.map((p) => {
        const tasks = flattenTasks(p);
        const done = tasks.filter((t) => doneIds.has(t.id)).length;
        return { total: tasks.length, done };
      }),
    [phases, doneIds],
  );

  // First phase with at least one incomplete task; if all done, last phase
  // wears the "done" marker.
  const firstIncomplete = stats.findIndex((s) => s.done < s.total);
  const currentIdx =
    firstIncomplete === -1 ? phases.length - 1 : firstIncomplete;

  return (
    <Stack spacing={1.5}>
      <Typography
        component="span"
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {t("PhaseMap.yourRoadmapLabel")}
      </Typography>
      <Stack spacing={1}>
        {phases.map((phase, i) => {
          const { total, done } = stats[i];
          const status: Status =
            i < currentIdx
              ? "done"
              : i === currentIdx
                ? done >= total && total > 0
                  ? "done"
                  : "current"
                : "upcoming";
          const dotColor =
            status === "done"
              ? theme.palette.success.main
              : status === "current"
                ? theme.palette.secondary.main
                : "transparent";
          const dotBorder =
            status === "upcoming"
              ? `1.5px solid ${theme.palette.divider}`
              : "none";

          return (
            <Stack
              key={phase.slug}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ fontSize: 12.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: dotColor,
                  border: dotBorder,
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />
              <Box
                component="span"
                sx={{
                  color:
                    status === "upcoming" ? "text.tertiary" : "text.primary",
                  fontWeight: status === "current" ? 500 : 400,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {phase.title}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                  fontSize: 11,
                  color: "text.tertiary",
                }}
              >
                {done}/{total}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
